import { useMemo, useState } from "react";
import {
  calculateExpectedCorpus,
  formatAsCrore,
  formatCompact,
} from "creso-core/utils";
import { AMOUNT_INPUT_MAX, rangeStepper } from "./config";
import { CalculatorInput } from "./calculator-input";
import { CalculatorChart } from "./calculator-chart";
import { CalculatorResultSummary } from "./result-summary";
import { Disclaimer } from "./disclaimer";
import { CalculatorResultContainer } from "./result-container";
import { CalculatorSnapshotContainer } from "./snapshot-container";
import { CalculatorInputSummary } from "./input-summary";
import { SnapshotButton } from "./snapshot-btn";

const DEFAULTS = {
  investmentAmount: 500_000,
  duration: 5,
  expectedReturn: 12,
};

export function LumpsumCalculator({
  brandRender,
}: {
  brandRender: () => React.ReactNode;
}) {
  const [investmentAmount, setInvestmentAmount] = useState(
    DEFAULTS.investmentAmount
  );
  const [duration, setDuration] = useState(DEFAULTS.duration);
  const [expectedReturn, setExpectedReturn] = useState(DEFAULTS.expectedReturn);

  const { maturityValue, gains, chartData } = useMemo(() => {
    const maturity = calculateExpectedCorpus(
      0,
      investmentAmount,
      expectedReturn,
      duration * 12
    );
    const gainAmount = maturity - investmentAmount;

    const points = Array.from({ length: duration }, (_, i) => {
      const year = i + 1;
      const corpusAtYear = calculateExpectedCorpus(
        0,
        investmentAmount,
        expectedReturn,
        year * 12
      );
      return {
        label: `Y${year}`,
        invested: investmentAmount,
        gains: Math.max(0, corpusAtYear - investmentAmount),
      };
    });

    return { maturityValue: maturity, gains: gainAmount, chartData: points };
  }, [investmentAmount, duration, expectedReturn]);

  const summaryItems = [
    { label: "Total Invested", value: formatCompact(investmentAmount) },
    { label: "Maturity Value", value: formatCompact(maturityValue) },
    {
      label: "Gains",
      value: formatCompact(gains),
      highlight: true,
    },
  ];

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <CalculatorInput
          label="Investment Amount"
          value={investmentAmount}
          onChange={setInvestmentAmount}
          min={10_000}
          max={1_00_00_000}
          inputMax={AMOUNT_INPUT_MAX}
          step={rangeStepper}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Duration"
          value={duration}
          onChange={setDuration}
          min={1}
          max={40}
          step={1}
          unit="yr"
        />
        <CalculatorInput
          label="Expected Returns"
          value={expectedReturn}
          onChange={setExpectedReturn}
          min={1}
          max={50}
          step={0.5}
          unit="%"
        />
      </div>

      <CalculatorSnapshotContainer
        brandRender={brandRender}
        inputRender={() => (
          <CalculatorInputSummary
            header="Lumpsum calculation"
            fields={[
              {
                label: "Investment Amount",
                value: formatAsCrore(investmentAmount),
              },
              { label: "Duration", value: `${duration} year(s)` },
              { label: "Expected Returns", value: `${expectedReturn}%` },
            ]}
          />
        )}
        resultRender={({ onSnapshot }) => (
          <CalculatorResultContainer>
            <CalculatorResultSummary items={summaryItems} />
            <div className="min-h-85 flex relative justify-center w-full pt-12 mb-2">
              <SnapshotButton onSnapshot={onSnapshot} />
              <CalculatorChart data={chartData} />
            </div>
            <Disclaimer />
          </CalculatorResultContainer>
        )}
      />
    </div>
  );
}
